import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, Camera, Lock, AlertTriangle, Activity, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { opsApi, money, pct } from "../../lib/api";
import BudgetTable from "./BudgetTable";
import OverrideDialog from "./OverrideDialog";
import DirectCostDialog from "./DirectCostDialog";
import HoursPanel from "./HoursPanel";
import AccelerationPanel from "../../components/AccelerationPanel";

const HEALTH_TONE = {
  ok: "border-slate-200 bg-white text-slate-700",
  warn: "border-amber-200 bg-amber-50 text-amber-800",
  bad: "border-rose-200 bg-rose-50 text-rose-800",
};
const GROUP_LABELS = { cost_code: "Cost code", cost_type: "Cost type", scope: "Scope" };

/** OPS-2 — the budget the PMs asked for: the SOV as a budget. Generated from the working
 *  estimate at Ready, locked at Open, moved only by Processed Approved change orders. */
export default function BudgetTab({ project, reload }) {
  const [data, setData] = useState(null);
  const [group, setGroup] = useState("cost_code");
  const [view, setView] = useState("profit");
  const [busy, setBusy] = useState(false);
  const [override, setOverride] = useState(null);
  const [direct, setDirect] = useState(null);
  const [snapshots, setSnapshots] = useState(null);

  const load = useCallback(async () => {
    try { setData(await opsApi.budget(project.id, group)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load the budget"); }
  }, [project.id, group]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const res = await opsApi.budgetGenerate(project.id);
      toast.success(`Budget ${res.generation === 1 ? "generated" : "rebuilt"} — ${res.rows} rows, `
        + `${money(res.contract_value)} allocated`);
      await load();
      reload?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not generate the budget");
    } finally { setBusy(false); }
  };

  const snapshot = async () => {
    setBusy(true);
    try {
      await opsApi.budgetSnapshot(project.id, { trigger: "manual", note: "Saved by hand" });
      toast.success("Snapshot saved");
      if (snapshots) setSnapshots(await opsApi.budgetSnapshots(project.id));
    } catch { toast.error("Could not snapshot the budget"); }
    finally { setBusy(false); }
  };

  const toggleSnapshots = async () => {
    if (snapshots) { setSnapshots(null); return; }
    try { setSnapshots(await opsApi.budgetSnapshots(project.id)); }
    catch { toast.error("Could not load the snapshots"); }
  };

  if (!data) {
    return <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading budget…
    </div>;
  }

  if (!data.exists) {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center"
             data-testid="ops-budget-empty">
          <h3 className="text-base font-semibold text-slate-900">No budget yet</h3>
          <p className="mx-auto mt-2 max-w-lg text-sm text-slate-500">
            The budget generates itself from the working estimate when the job is set Ready —
            one row per cost code and cost type, with the contract value allocated by the estimate's
            sell. You can also generate it now.
          </p>
          {data.can_edit !== false && (
            <Button className="mt-4" size="sm" onClick={generate} disabled={busy}
                    data-testid="ops-budget-generate">
              {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />} Generate the budget
            </Button>
          )}
        </div>
        {/* An accepted overtime allowance stands on its own — it does not wait for a budget. */}
        <AccelerationPanel projectId={project.id} mode="allowance" />
      </div>
    );
  }

  const t = data.totals;

  return (
    <div className="space-y-4" data-testid="ops-budget-tab">
      {/* header health line */}
      <div className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 ${HEALTH_TONE[data.health.tone]}`}
           data-testid="ops-budget-health">
        <Activity className="h-4 w-4 shrink-0" />
        <span className="text-sm font-semibold" data-testid="ops-budget-health-line">{data.health.line}</span>
        <span className="text-xs opacity-80">
          Revised contract {money(t.revised_contract)} · revised budget {money(t.revised_budget)} ·
          current margin {pct(t.current_margin_pct)}
        </span>
        {data.locked && (
          <span className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white/70 px-2 py-0.5 text-xs"
                data-testid="ops-budget-locked">
            <Lock className="h-3 w-3" /> Locked at Open
          </span>
        )}
        <span className="ml-auto text-xs opacity-70">
          v{data.generation} · {(data.generated_at || "").slice(0, 10)} · {data.generated_by}
        </span>
      </div>

      {data.estimate_changed && !data.locked && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
             data-testid="ops-budget-estimate-changed">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span>The working estimate changed{data.estimate_changed_at
            ? ` on ${data.estimate_changed_at.slice(0, 10)}` : ""} and the base budget was rebuilt
            from it. PM overrides, direct costs and change order impact were kept.</span>
          {data.can_edit && (
            <Button size="sm" variant="outline" className="ml-auto h-7" onClick={generate} disabled={busy}
                    data-testid="ops-budget-rebuild">Rebuild now</Button>
          )}
        </div>
      )}
      {data.estimate_changed && data.locked && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
             data-testid="ops-budget-locked-changed">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          The working estimate moved after this budget locked. The budget did not change —
          scope changes belong on a change order.
        </div>
      )}

      {/* summary tiles */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {[["Base contract", t.base_contract], ["Base budget", t.base_budget],
          ["Submitted / Processed Unapproved", t.pending_revenue], ["Current profit", t.current_profit],
          ["Gain / fade", t.gain_fade]].map(([k, v]) => (
          <div key={k} className="rounded-lg border border-slate-200 bg-white px-4 py-3"
               data-testid={`ops-budget-tile-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <p className="text-xs text-slate-500">{k}</p>
            <p className={`text-lg font-semibold ${v < 0 ? "text-rose-700" : "text-slate-900"}`}>
              {money(v)}
            </p>
          </div>
        ))}
      </div>

      {/* controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
          {[["profit", "Profit"], ["cost", "Cost & forecast"]].map(([k, label]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`ops-budget-view-${k}`}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      view === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          Group by
          <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
            {(data.groupings || []).map((k) => (
              <button key={k} onClick={() => setGroup(k)} data-testid={`ops-budget-group-by-${k}`}
                      className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                        group === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
                {GROUP_LABELS[k]}
              </button>
            ))}
          </div>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={toggleSnapshots} data-testid="ops-budget-snapshots-toggle">
            <History className="mr-1.5 h-3.5 w-3.5" /> Snapshots
          </Button>
          {data.can_override && (
            <Button variant="outline" size="sm" onClick={snapshot} disabled={busy}
                    data-testid="ops-budget-snapshot">
              <Camera className="mr-1.5 h-3.5 w-3.5" /> Snapshot now
            </Button>
          )}
          {data.can_edit && (
            <Button variant="outline" size="sm" onClick={generate} disabled={busy}
                    data-testid="ops-budget-generate">
              {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />} Rebuild from estimate
            </Button>
          )}
        </div>
      </div>

      {snapshots && (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white"
             data-testid="ops-budget-snapshots">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-1.5 text-left">Taken</th>
                <th className="px-3 py-1.5 text-left">Trigger</th>
                <th className="px-3 py-1.5 text-right">Revised contract</th>
                <th className="px-3 py-1.5 text-right">Revised budget</th>
                <th className="px-3 py-1.5 text-right">Profit</th>
                <th className="px-3 py-1.5 text-left">By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {snapshots.snapshots.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-4 text-center text-slate-500">
                  No snapshots yet — one is taken automatically on every change order approval,
                  every pay application and when the job opens.
                </td></tr>
              )}
              {snapshots.snapshots.map((s) => (
                <tr key={s.id} data-testid={`ops-budget-snapshot-${s.id}`}>
                  <td className="px-4 py-1.5">{(s.taken_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-1.5">{s.trigger}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{money(s.totals?.revised_contract)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{money(s.totals?.revised_budget)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{money(s.totals?.current_profit)}</td>
                  <td className="px-3 py-1.5">{s.by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BudgetTable project={project} data={data} view={view}
                   onOverride={(row, field) => setOverride({ row, field: field || "eac" })} onDirectCost={setDirect} onChanged={load} />

      <p className="text-xs text-slate-500">
        Hover any figure to see how it was worked out. Expand a row for the estimate lines and FRS
        rooms behind it. Pending change orders are shown on their own and never inside Revised —
        only Processed Approved change orders move this budget.
      </p>

      <HoursPanel hours={data.hours} />
      <AccelerationPanel projectId={project.id} mode="allowance" />

      {override && (
        <OverrideDialog project={project} row={override.row} initialField={override.field} onClose={() => setOverride(null)}
                        onDone={() => { setOverride(null); load(); }} />
      )}
      {direct && (
        <DirectCostDialog project={project} row={direct} canEdit={data.can_override}
                          onClose={() => setDirect(null)} onDone={load} />
      )}
    </div>
  );
}
